import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Plus, RotateCcw, Search } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { PostcodeChip } from "@/components/silver/tool/PostcodeSearch";
import { PresetGrid } from "@/components/silver/tool/PresetGrid";
import { LogicSummary } from "@/components/silver/tool/LogicSummary";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { RollingNumber } from "@/components/silver/common/RollingNumber";
import { formatGBP } from "@/config/pricing";

const EASE = [0.22, 1, 0.36, 1];
const SAMPLE = ["RG40", "RG41", "SW18"];

function PatchChips() {
  const { areas, audience, removeArea, addAreas, setSearchOpen } = useSilver();
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="patch-chips">
      <AnimatePresence initial={false}>
        {areas.map((c) => <PostcodeChip key={c} code={c} onRemove={removeArea} size="sm" />)}
      </AnimatePresence>
      <button type="button" className="pill !py-1.5" onClick={() => setSearchOpen(true)} data-testid="add-districts-btn">
        <Plus className="h-3.5 w-3.5" /> {areas.length ? "Add districts" : "Add postcode districts"}
      </button>
      {!areas.length && (
        <span className="ml-1 flex flex-wrap items-center gap-2 text-[13px] text-white/45" data-testid="empty-patch-state">
          or try
          {SAMPLE.map((c) => <button key={c} type="button" className="pill !py-1" onClick={() => addAreas([c])} data-testid={`sample-postcode-${c}`}>{c}</button>)}
          <button type="button" className="pill !py-1" onClick={() => addAreas(["RG40", "RG41"])} data-testid="sample-postcode-pair">RG40 + RG41</button>
        </span>
      )}
      {areas.length > 0 && (
        <span className="ml-1 text-[13px] text-white/55" data-testid="patch-summary">
          <AnimatedNumber value={audience.silverTotal} duration={0.8} className="num font-semibold text-white" /> homeowner households
        </span>
      )}
    </div>
  );
}

// Always-visible estimate + Build button, so the number and the next step never scroll out of view.
function DefineBar() {
  const { areas, audience, price, setPhase, setSearchOpen, resetFilters } = useSilver();
  const noAreas = areas.length === 0;
  const noMatch = !noAreas && audience.total === 0;
  return (
    <motion.div className="fixed inset-x-3 bottom-4 z-40 flex justify-center sm:inset-x-0" initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0, transition: { duration: 0.2 } }} transition={{ type: "spring", stiffness: 260, damping: 28, delay: 0.3 }} data-testid="define-action-bar">
      <div className="glass-strong flex w-full max-w-[820px] items-center justify-between gap-3 !rounded-full p-2 sm:pl-3">
        <button type="button" className="btn-ghost !px-4 !py-3 text-[13px]" onClick={() => setSearchOpen(true)} data-testid="define-bar-edit-postcodes-btn">
          <Search className="h-4 w-4 text-[#C6EE5A]" /> <span className="hidden sm:inline">Edit postcodes</span>
        </button>
        <p className="min-w-0 truncate text-center text-[13px] text-white/60" data-testid="define-bar-summary">
          {noAreas ? "Add postcode districts to begin" : noMatch ? "No matching households for these filters" : (
            <>
              <AnimatedNumber value={audience.total} className="num text-[15px] font-semibold text-white" data-testid="estimated-audience-total" /> households
              <span className="hidden sm:inline"> · from <RollingNumber value={price.baseCost} format={formatGBP} className="font-semibold text-[#C6EE5A]" /></span>
            </>
          )}
        </p>
        {noMatch ? (
          <button type="button" className="btn-ghost !px-5 !py-3 text-[14px]" onClick={resetFilters} data-testid="broaden-audience-btn"><RotateCcw className="h-4 w-4" /> Broaden</button>
        ) : (
          <button type="button" className="btn-primary !px-5 !py-3 text-[14px] sm:!px-6" disabled={noAreas} onClick={() => setPhase("analysing")} data-testid="build-my-audience-cta">
            <span className="hidden sm:inline">Build the audience</span><span className="sm:hidden">Build</span> <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </motion.div>
  );
}

export function DefineView() {
  return (
    <motion.div key="define" className="pb-24" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, transition: { duration: 0.25 } }} transition={{ duration: 0.8, ease: EASE }} data-testid="define-view">
      <p className="eyebrow">Your patch</p>
      <div className="mt-3"><PatchChips /></div>

      <div className="mt-7 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <h1 className="text-3xl font-extrabold leading-[1.05] tracking-[-0.04em] sm:text-4xl lg:whitespace-nowrap lg:text-[2.5rem]">Select your homeowner audience.</h1>
        <div className="lg:flex lg:justify-end" data-testid="define-logic"><LogicSummary compact /></div>
      </div>

      <div className="mt-6"><PresetGrid /></div>

      <DefineBar />
    </motion.div>
  );
}
